import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, DatePicker, Select, Table, Tooltip } from 'antd';
import { useConnection } from '../../connection/connection';
import {
  BusinessContextData,
  useBusinessContext,
} from '../../context/BusinessContext';
import { DayJs, Utils } from '../../common/Utils';
import classNames from 'classnames';
import { LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { report } from 'process';
import GeneralLedgerDialog, {
  useGeneralLedgerDialog,
} from '../GeneralLedger/GeneralLedgerDialog';

type Props = {};

interface Balance {
  accountId: string;
  balance: number;
  balanceInBusinessCcy: number;
}

const getBalanceInBusinessCcy = async (
  business: BusinessContextData,
  accountId: string,
  amount: number,
): Promise<Balance> => {
  const acc = business.accounts[accountId];
  let fcyAmount = amount;
  if (acc.currency !== business.currency) {
    const rate = (
      await business.getCurrencyRate(acc.currency, business.currency)
    ).rate;
    fcyAmount = amount * rate;
  }

  return {
    accountId: acc.accountId,
    balance: amount,
    balanceInBusinessCcy: fcyAmount,
  };
};

const isLegibleAmount = (amount: number): boolean =>
  amount > 0.01 || amount < -0.01;

export const TrialBalance: FC<Props> = (props) => {
  const connection = useConnection();
  const business = useBusinessContext();
  const generalLedgerDialog = useGeneralLedgerDialog();
  const hasInitialized = useRef(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [siteId, setSiteId] = useState<string>('0');
  const [selectedYear, setSelectedYear] = useState<number>(DayJs().year());
  const [reportDate, setReportDate] = useState<DayJs.Dayjs>(DayJs());
  const [selectedReportType, setSelectedReportType] =
    useState<string>('accrual');
  const [balances, setBalances] = useState<Balance[]>([]);

  const currentDate = useMemo(() => DayJs(), []);

  const siteOptions = useMemo(() => {
    return [
      { label: 'All Sites', value: '0' },
      {
        label: 'Sites',
        options: Object.values(business.sites).map((s) => ({
          label: s.name,
          value: s.siteId,
        })),
      },
    ];
  }, [business.sites]);

  const reportYears = useMemo(() => {
    const financialYears = [...new Array(5).keys()].map((y) =>
      DayJs().subtract(y, 'year'),
    );
    return financialYears.map((y) => ({
      label: y.format('YYYY'),
      value: y.year(),
    }));
  }, []);

  const reportTypeOptions = useMemo(() => {
    return [
      { label: 'Accrual', value: 'accrual' },
      { label: 'Cash Basis', value: 'cash-basis' },
    ];
  }, []);

  const updateReport = useCallback(() => {
    setLoading(true);
    connection
      .get(`accounts/${business.businessId}/balances`, {
        params: {
          site: siteId == '0' ? undefined : siteId,
          accounts: business
            .getAccountsInSite(siteId)
            .filter((a) => a.activity)
            .map((a) => a.accountId)
            .join(','),
          date: reportDate.format('YYYY-MM-DD'),
        },
      })
      .then(async (res) => {
        const data = res.data as Record<string, number>;
        const entryPromises = Object.entries(data).map(
          async ([accountId, balance]) =>
            await getBalanceInBusinessCcy(business, accountId, balance),
        );
        const entries = await Promise.all(entryPromises);
        setBalances(entries);
      })
      .finally(() => setLoading(false));
  }, [siteId, business, connection, reportDate]);

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      updateReport();
    }
  }, [updateReport, hasInitialized]);

  const renderCcyAmount = useCallback(
    (record: Balance, isDebit: boolean) => {
      const amount =
        (isDebit && record.balanceInBusinessCcy > 0) ||
        (!isDebit && record.balanceInBusinessCcy < 0)
          ? record.balanceInBusinessCcy
          : 0;
      const account = business.accounts[record.accountId];
      const formattedAmount = Utils.formatPrice(amount);
      if (account.currency === business.currency || !isLegibleAmount(amount)) {
        return formattedAmount;
      }

      const accCcyFormattedAmount = Utils.formatPrice(record.balance);
      return (
        <Tooltip title={`${account.currency} ${accCcyFormattedAmount}`}>
          {formattedAmount}
        </Tooltip>
      );
    },
    [business.accounts, business.currency],
  );

  const columns = useMemo(() => {
    return [
      {
        key: 'account',
        title: 'ACCOUNTS',
        width: 120,
        dataIndex: '',
        render: (text: any, record: Balance) => {
          const from = `${selectedYear}-01-01`;
          const to = reportDate.format('YYYY-MM-DD');
          return (
            <Link
              to={``}
              onClick={() => {
                generalLedgerDialog.current?.open(
                  record.accountId,
                  DayJs(from),
                  DayJs(to),
                );
              }}
            >
              {business.accounts[record.accountId].name} {<LinkOutlined />}
            </Link>
          );
        },
      },
      {
        key: 'debit',
        title: `DEBIT (${business.currency})`,
        dataIndex: 'amount',
        align: 'right',
        width: 160,
        render: (val: number, record: Balance) => renderCcyAmount(record, true),
      },
      {
        key: 'credit',
        title: `CREDIT (${business.currency})`,
        dataIndex: 'amount',
        align: 'right',
        width: 160,
        render: (val: number, record: Balance) =>
          renderCcyAmount(record, false),
      },
    ] as any;
  }, [
    business.accounts,
    business.currency,
    renderCcyAmount,
    reportDate,
    selectedYear,
    generalLedgerDialog,
  ]);

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>Trial Balance</WebUxTitle>
      <GeneralLedgerDialog ref={generalLedgerDialog} />
      <div className="flex flex-col gap-2">
        <div className="flex justify-between gap-1">
          <div className="flex gap-10">
            <div className="flex flex-col gap-1">
              <label>Site</label>
              <Select
                className="w-[150px]"
                options={siteOptions}
                value={siteId}
                onChange={setSiteId}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label>Up until</label>
              <div className="flex gap-2">
                <Select
                  className="w-[150px]"
                  options={reportYears}
                  value={selectedYear}
                  onChange={(year) => {
                    setSelectedYear(year);
                    const selectedReportDate = DayJs(reportDate);
                    if (selectedReportDate.year() !== year) {
                      setReportDate(selectedReportDate.year(year));
                    }
                  }}
                />
                <DatePicker
                  value={reportDate}
                  allowClear={false}
                  onChange={(date: DayJs.Dayjs) => {
                    setReportDate(date);
                    if (date.year() !== reportDate.year()) {
                      setSelectedYear(date.year());
                    }
                  }}
                  disabledDate={(date) => date.isAfter(currentDate)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label>Report Type</label>
              <div className="flex gap-10">
                <Select
                  className="w-[150px]"
                  options={reportTypeOptions}
                  value={selectedReportType}
                  onChange={setSelectedReportType}
                />
                <Button type="primary" onClick={() => updateReport()}>
                  Update Report
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Table
          loading={loading}
          dataSource={balances}
          columns={columns}
          pagination={false}
          onRow={(data, index) => ({
            className: classNames({
              'bg-slate-100': (data as any).type === 'total',
            }),
          })}
        />
      </div>
    </div>
  );
};
